import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Loader2, Trash2, Flame, Clock, Ruler, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { API, getLocalDateString } from '../App';

const INTENSITIES = [
  { value: 'light', label: 'LIGHT' },
  { value: 'moderate', label: 'MODERATE' },
  { value: 'vigorous', label: 'VIGOROUS' },
];

const LogActivity = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialActivityType =
    searchParams.get('activity') === 'Running' ? 'Running' : 'Walking';
  const [catalog, setCatalog] = useState({ types: [], distance_activities: [] });
  const [history, setHistory] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);

  // form fields
  const [activityType, setActivityType] = useState(initialActivityType);
  const [date, setDate] = useState(getLocalDateString());
  const [duration, setDuration] = useState('');
  const [intensity, setIntensity] = useState('moderate');
  const [distance, setDistance] = useState('');
  const [distanceUnit, setDistanceUnit] = useState('mi');
  const [calories, setCalories] = useState('');
  const [caloriesAuto, setCaloriesAuto] = useState(true);
  const [notes, setNotes] = useState('');
  const [shareToFeed, setShareToFeed] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  // Share-after modal
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareTargetMessage, setShareTargetMessage] = useState('');
  const [sharing, setSharing] = useState(false);

  const estimateTimer = useRef(null);

  const usesDistance = catalog.distance_activities?.includes(activityType);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [typesRes, histRes] = await Promise.all([
          axios.get(`${API}/activities/types`, { headers }),
          axios.get(`${API}/activities/history?limit=10`, { headers }),
        ]);
        setCatalog(typesRes.data);
        setHistory(histRes.data.history || []);
      } catch (e) {
        console.error('Failed to load activity catalog', e);
        toast.error('Failed to load activity types.');
      } finally {
        setLoadingCatalog(false);
      }
    })();
  }, []);

  // Auto-estimate calories whenever the relevant fields change
  useEffect(() => {
    if (!caloriesAuto) return;
    const d = parseFloat(duration);
    if (!activityType || !d || d <= 0) {
      setCalories('');
      return;
    }
    if (estimateTimer.current) clearTimeout(estimateTimer.current);
    estimateTimer.current = setTimeout(async () => {
      try {
        setEstimating(true);
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/activities/estimate-calories`,
          { activity_type: activityType, duration_minutes: d, intensity },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCalories(String(res.data.calories));
      } catch (e) {
        // silent — user can still override
      } finally {
        setEstimating(false);
      }
    }, 350);
    return () => estimateTimer.current && clearTimeout(estimateTimer.current);
  }, [activityType, duration, intensity, caloriesAuto]);

  const handleCaloriesChange = (val) => {
    setCalories(val);
    setCaloriesAuto(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const d = parseFloat(duration);
    if (!activityType || !d || d <= 0) {
      toast.error('Please enter activity type and duration.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        activity_type: activityType,
        date,
        duration_minutes: d,
        intensity,
        calories_burned: calories ? parseFloat(calories) : null,
        distance_value: usesDistance && distance ? parseFloat(distance) : null,
        distance_unit: distanceUnit,
        notes: notes || null,
        share_to_feed: shareToFeed,
        share_message: shareToFeed && shareMessage.trim() ? shareMessage.trim() : null,
      };
      await axios.post(`${API}/activities/log`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${activityType} logged.${shareToFeed ? ' Shared with friends.' : ''}`);
      // reset
      setDuration('');
      setDistance('');
      setNotes('');
      setCalories('');
      setCaloriesAuto(true);
      setShareMessage('');
      // refresh history
      const histRes = await axios.get(`${API}/activities/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(histRes.data.history || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log activity.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/activities/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory((h) => h.filter((a) => a.id !== id));
      toast.success('Activity removed.');
    } catch (e) {
      toast.error('Failed to delete activity.');
    }
  };

  const openShareModal = (activity) => {
    setShareTarget(activity);
    setShareTargetMessage('');
    setShareModalOpen(true);
  };

  const handleShareSubmit = async () => {
    if (!shareTarget) return;
    setSharing(true);
    try {
      const token = localStorage.getItem('token');
      const summary = {
        activity_type: shareTarget.activity_type,
        duration_minutes: shareTarget.duration_minutes,
        distance_value: shareTarget.distance_value,
        distance_unit: shareTarget.distance_unit,
        calories_burned: shareTarget.calories_burned,
        intensity: shareTarget.intensity,
      };
      await axios.post(
        `${API}/feed/share`,
        {
          kind: 'activity',
          ref_id: shareTarget.id,
          message: shareTargetMessage.trim() || null,
          summary,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Shared with friends.');
      setShareModalOpen(false);
      setShareTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to share.');
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 p-4 sm:p-6" data-testid="log-activity-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/workout-selection')}
            className="glass border-stone-700 text-stone-300 hover:bg-stone-800 hover:border-stone-600"
            data-testid="log-activity-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-900 to-emerald-950 border-2 border-emerald-700 mb-4 shadow-lg shadow-emerald-900/40">
            <MapPin className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-wider">LOG ACTIVITY</h1>
          <p className="text-stone-400 mt-2 uppercase tracking-wide text-sm">
            Trail walks, runs, sports — log anything you do outside the app.
          </p>
        </div>

        {/* Form */}
        <Card className="glass border-stone-800 p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Activity</label>
                <Select value={activityType} onValueChange={setActivityType} disabled={loadingCatalog}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="activity-type-select">
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white max-h-72">
                    {catalog.types.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-stone-800 focus:text-white">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Date</label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-stone-900 border-stone-700 text-white"
                  data-testid="activity-date-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Duration (min)</label>
                <Input
                  type="number"
                  min="1"
                  max="1440"
                  step="1"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 45"
                  className="bg-stone-900 border-stone-700 text-white"
                  data-testid="activity-duration-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Intensity</label>
                <div className="flex gap-1.5">
                  {INTENSITIES.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setIntensity(opt.value)}
                      className={`flex-1 py-2 text-xs font-bold rounded-md tracking-wider transition-all border ${
                        intensity === opt.value
                          ? 'bg-emerald-800 border-emerald-600 text-white'
                          : 'bg-stone-900 border-stone-700 text-stone-400 hover:text-white hover:border-stone-600'
                      }`}
                      data-testid={`activity-intensity-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {usesDistance && (
                <div className="sm:col-span-2 grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Distance (optional)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={distance}
                      onChange={(e) => setDistance(e.target.value)}
                      placeholder="e.g. 3.2"
                      className="bg-stone-900 border-stone-700 text-white"
                      data-testid="activity-distance-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Unit</label>
                    <Select value={distanceUnit} onValueChange={setDistanceUnit}>
                      <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="activity-distance-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-900 border-stone-700 text-white">
                        <SelectItem value="mi" className="focus:bg-stone-800 focus:text-white">mi</SelectItem>
                        <SelectItem value="km" className="focus:bg-stone-800 focus:text-white">km</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="flex items-center justify-between text-xs uppercase tracking-wider text-stone-400 mb-1.5">
                  <span>Calories Burned</span>
                  <span className="text-[10px] text-stone-500 normal-case tracking-normal">
                    {caloriesAuto ? (estimating ? 'estimating…' : 'auto-estimated • editable') : 'manual'}
                  </span>
                </label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={calories}
                  onChange={(e) => handleCaloriesChange(e.target.value)}
                  placeholder="e.g. 250"
                  className="bg-stone-900 border-stone-700 text-white"
                  data-testid="activity-calories-input"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Notes (optional)</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Trail name, route, weather, how it felt…"
                  rows={2}
                  maxLength={500}
                  className="bg-stone-900 border-stone-700 text-white"
                  data-testid="activity-notes-input"
                />
              </div>

              <div className="sm:col-span-2 border-t border-stone-800 pt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="share-to-feed"
                    checked={shareToFeed}
                    onCheckedChange={(v) => setShareToFeed(!!v)}
                    className="mt-0.5 border-stone-600 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-600"
                    data-testid="activity-share-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="share-to-feed" className="text-sm text-stone-200 font-semibold flex items-center gap-1.5 cursor-pointer">
                      <Share2 className="w-3.5 h-3.5 text-emerald-400" />
                      Share to friends feed
                    </label>
                    <p className="text-xs text-stone-500 mt-0.5">Let friends see this win. An auto-generated post will be created.</p>
                  </div>
                </div>
                {shareToFeed && (
                  <div className="mt-3">
                    <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Custom message (optional)</label>
                    <Textarea
                      value={shareMessage}
                      onChange={(e) => setShareMessage(e.target.value)}
                      placeholder="Add your own caption — leave blank to auto-generate."
                      rows={2}
                      maxLength={500}
                      className="bg-stone-900 border-stone-700 text-white"
                      data-testid="activity-share-message"
                    />
                  </div>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-gradient-to-r from-emerald-800 to-emerald-950 hover:from-emerald-700 hover:to-emerald-900 text-white font-bold py-6 rounded-lg border-2 border-emerald-700 hover:border-emerald-600 shadow-lg shadow-emerald-900/40 uppercase tracking-widest"
              data-testid="activity-submit-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Log Activity'
              )}
            </Button>
          </form>
        </Card>

        {/* History */}
        <div>
          <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-3">Recent Activities</h2>
          {history.length === 0 ? (
            <Card className="glass border-stone-800 p-6 text-center">
              <p className="text-stone-400 text-sm">No activities logged yet. Your trail walks will appear here.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {history.map((a) => (
                <Card key={a.id} className="glass border-stone-800 p-4" data-testid={`activity-history-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-white font-bold tracking-wide">{a.activity_type}</span>
                        <span className="text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-800 px-1.5 py-0.5 rounded">
                          {a.intensity}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-400">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{Math.round(a.duration_minutes)} min</span>
                        <span className="flex items-center gap-1"><Flame className="w-3 h-3" />{Math.round(a.calories_burned)} cal</span>
                        {a.distance_value ? (
                          <span className="flex items-center gap-1"><Ruler className="w-3 h-3" />{a.distance_value} {a.distance_unit}</span>
                        ) : null}
                        <span>{a.date}</span>
                      </div>
                      {a.notes && <p className="text-xs text-stone-500 mt-1.5 italic">{a.notes}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => openShareModal(a)}
                      className="text-stone-500 hover:text-emerald-400 transition-colors shrink-0"
                      data-testid={`activity-share-${a.id}`}
                      aria-label="Share activity"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(a.id)}
                      className="text-stone-500 hover:text-blood-500 transition-colors shrink-0"
                      data-testid={`activity-delete-${a.id}`}
                      aria-label="Delete activity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Share modal */}
      {shareModalOpen && shareTarget && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => !sharing && setShareModalOpen(false)}
          data-testid="share-modal"
        >
          <Card
            className="glass border-stone-800 p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-bold text-white tracking-wider">SHARE TO FRIENDS</h3>
            </div>
            <p className="text-xs text-stone-400 mb-3 italic">
              Auto-preview: Logged a {shareTarget.activity_type} · {Math.round(shareTarget.duration_minutes)} min
              {shareTarget.distance_value ? ` · ${shareTarget.distance_value} ${shareTarget.distance_unit}` : ''}
              {shareTarget.calories_burned ? ` · ${Math.round(shareTarget.calories_burned)} cal` : ''}
            </p>
            <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Custom message (optional)</label>
            <Textarea
              value={shareTargetMessage}
              onChange={(e) => setShareTargetMessage(e.target.value)}
              placeholder="Add your own caption…"
              rows={3}
              maxLength={500}
              className="bg-stone-900 border-stone-700 text-white mb-4"
              data-testid="share-modal-message"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShareModalOpen(false)}
                disabled={sharing}
                className="flex-1 border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800 hover:text-white"
                data-testid="share-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleShareSubmit}
                disabled={sharing}
                className="flex-1 bg-emerald-700 hover:bg-emerald-600 text-white font-bold"
                data-testid="share-modal-submit"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Share'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LogActivity;
